import type { ColumnDirection } from '@hotjar/hotsauce';
import { Table, tokens } from '@hotjar/hotsauce';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigation } from '@hotjar/navigation';
import { useTranslation } from '@hotjar/translations';

export interface NpsValue {
  label: number;
  value: number;
  percentage: string;
}

export interface NPSTableProps {
  npsValues: NpsValue[] | null;
  isSticky?: boolean;
}

const borderWidth = '2px';

const StyledTable = styled(Table)`
  border-collapse: collapse;
`;

const StyledCell = styled(Table.Cell)<{ borderColor: string }>`
  border-left: ${borderWidth} solid ${(props) => props.borderColor};
`;

const StyledRow = styled(Table.Row)<{ highlighted?: boolean }>`
  ${(props) =>
    props.highlighted &&
    `
      background: ${tokens.color.bg_info};
      td {
        font-weight: 600;
      }
    `}
`;

const StyledHeaderCell = styled(Table.HeadCell)<{ sortDirection: ColumnDirection }>`
  border-left: ${borderWidth} solid
    ${(props) =>
      props.sortDirection !== 'none'
        ? tokens.BRAND_COLORS.neutral_200
        : tokens.BRAND_COLORS.neutral_100};
  &:hover {
    border-left-color: ${tokens.color.ui_border_light};
  }
`;

function sortData(data: NpsValue[], key: keyof NpsValue, direction: ColumnDirection): NpsValue[] {
  return data.slice().sort((a, b) => {
    if (a[key] === b[key]) return 0;
    return (a[key] > b[key] ? -1 : 1) * (direction === 'asc' ? -1 : 1);
  });
}

function getMaxScore(npsValues: NpsValue[]): number {
  return npsValues.reduce((maxScore, { value }) => Math.max(maxScore, value), 0);
}

function getNpsColor(npsValue: number): string {
  if (npsValue < 7) return tokens.color.crimson_light;
  if (npsValue < 9) return tokens.BRAND_COLORS.cobalt_500;
  return tokens.BRAND_COLORS.mint_500;
}

export function NPSTable({ npsValues, isSticky = true }: NPSTableProps): JSX.Element {
  const [sort, setSort] = useState<{ direction: ColumnDirection; key: keyof NpsValue }>({
    direction: 'desc',
    key: 'label',
  });
  const [data, setData] = useState<NpsValue[]>([]);
  const [maxScore, setMaxScore] = useState<number>(0);
  const { t } = useTranslation('feedback');

  function getSortState(key: string): ColumnDirection {
    if (key === sort.key) return sort.direction;
    return 'none';
  }

  function onSortChange(key: keyof NpsValue, direction: ColumnDirection): void {
    setSort({ key, direction });
  }

  useEffect(() => {
    if (npsValues === null) return;
    setData(sortData(npsValues, sort.key === 'percentage' ? 'value' : sort.key, sort.direction));
    setMaxScore(getMaxScore(npsValues));
  }, [npsValues, sort.direction, sort.key]);

  const { topBarHeight: topNavHeight } = useNavigation();

  return (
    <StyledTable
      caption={t('results.nps-view-mode.nps-table-caption')}
      tableName={t('results.nps-view-mode.nps-table-name')}
    >
      <Table.Head>
        <Table.HeadRow isSticky={isSticky} stickyOffset={topNavHeight}>
          <StyledHeaderCell
            isSortable
            sortDirection={getSortState('label')}
            onSort={(direction) => onSortChange('label', direction)}
          >
            {t('results.nps-view-mode.nps')}
          </StyledHeaderCell>
          <Table.HeadCell
            isSortable
            sortDirection={getSortState('value')}
            onSort={(direction) => onSortChange('value', direction)}
          >
            {t('surveys.answer-table.count')}
          </Table.HeadCell>
          <Table.HeadCell
            isSortable
            sortDirection={getSortState('percentage')}
            onSort={(direction) => onSortChange('percentage', direction)}
          >
            {t('common.percent-sign')}
          </Table.HeadCell>
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        {data.map((npsValue) =>
          maxScore === npsValue.value ? (
            <StyledRow
              key={npsValue.label}
              highlighted
              aria-label={t('surveys.answer-table.most-frequent')}
            >
              <StyledCell borderColor={getNpsColor(npsValue.label)}>{npsValue.label}</StyledCell>
              <Table.Cell>{npsValue.value}</Table.Cell>
              <Table.Cell>{npsValue.percentage}</Table.Cell>
            </StyledRow>
          ) : (
            <StyledRow key={npsValue.label}>
              <StyledCell borderColor={getNpsColor(npsValue.label)}>{npsValue.label}</StyledCell>
              <Table.Cell>{npsValue.value}</Table.Cell>
              <Table.Cell>{npsValue.percentage}</Table.Cell>
            </StyledRow>
          ),
        )}
      </Table.Body>
    </StyledTable>
  );
}
